import { useMutation } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { useNotifications } from '@/stores/useNotificationStore';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'usePeopleSyncMutation' });

interface SyncResult {
  totalFetched: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  errorDetails: Array<{ contactId: string; error: string }>;
  durationMs: number;
  syncedAt: string;
}

interface SyncTriggerRequest {
  updateExisting?: boolean;
  dryRun?: boolean;
  contactIds?: string[];
}

interface SyncTriggerResponse {
  success: boolean;
  workflowId: string;
  message?: string;
  statusEndpoint?: string;
  error?: string;
}

interface SyncStatusResponse {
  workflowId: string;
  status: string;
  startTime?: string;
  result?: SyncResult;
}

interface LastSyncResponse {
  success: boolean;
  workflowId: string | null;
  startTime: string | null;
  closeTime: string | null;
  rateLimitMinutes: number;
}

/**
 * Trigger people sync workflow asynchronously
 */
export const useTriggerPeopleSync = () => {
  const notifications = useNotifications();

  return useMutation<SyncTriggerResponse, unknown, SyncTriggerRequest>({
    mutationFn: async (request: SyncTriggerRequest) => {
      log.info('Triggering people sync', { request });
      const response = await httpClient.post<SyncTriggerResponse>(
        '/api/rest/app/v1/person/async',
        request,
      );
      return response;
    },
    onSuccess: (data) => {
      log.info('People sync triggered successfully', { workflowId: data.workflowId });
      notifications.success(
        'Sync Started',
        data.message || 'People sync workflow has been started',
      );
    },
    onError: (error: unknown) => {
      log.error('Failed to trigger people sync', { error });
      const normalizedError =
        typeof error === 'object' && error !== null
          ? (error as { error?: string; message?: string })
          : {};
      const errorMessage =
        normalizedError.error || normalizedError.message || 'Failed to start sync workflow';
      notifications.error('Sync Failed', errorMessage);
    },
  });
};

/**
 * Check status of people sync workflow
 */
export const useCheckSyncStatus = () => {
  return useMutation<SyncStatusResponse, unknown, string>({
    mutationFn: async (workflowId: string) => {
      log.debug('Checking sync status', { workflowId });
      const response = await httpClient.get<SyncStatusResponse>(
        `/api/rest/app/v1/person/async/${workflowId}`,
      );
      return response;
    },
    onError: (error) => {
      log.error('Failed to check sync status', { error });
    },
  });
};

/**
 * Get last completed sync info
 */
export const useGetLastSync = () => {
  return useMutation<LastSyncResponse, unknown, void>({
    mutationFn: async () => {
      log.debug('Fetching last sync info');
      const response = await httpClient.get<LastSyncResponse>('/api/rest/app/v1/person/async/last');
      return response;
    },
    onError: (error) => {
      log.warn('Failed to fetch last sync info', { error });
    },
  });
};
